import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  TextField,
  Button,
  Tabs,
  Tab,
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import dayjs from "dayjs";
import { createNotification, removeNotification, fetchNotifications } from "../../store/slices/dashboard/notificationSlice";
import styles from "./NotificationModal.module.css";

const NotificationModal = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const notifications = useSelector((state) => state.notificationState.notifications);
  const [activeTab, setActiveTab] = useState(0);
  const [notificationText, setNotificationText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      dispatch(fetchNotifications());
    }
  }, [open, dispatch]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    if (newValue === 1) {
      dispatch(fetchNotifications());
    }
  };

  const handleConfirm = async () => {
    if (notificationText.trim() && !isSubmitting) {
      setIsSubmitting(true);
      try {
        await dispatch(createNotification(notificationText));
        setNotificationText("");
        setActiveTab(1);
        dispatch(fetchNotifications());
      } catch (error) {
        console.error("Error creating notification:", error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleCancel = () => {
    setNotificationText("");
    onClose();
  };

  const handleDelete = (id) => {
    dispatch(removeNotification(id));
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: { borderRadius: 8 }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Notification</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        <Box sx={{ borderBottom: 1, borderColor: "divider", marginBottom: 2 }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab label="New Notification" />
            <Tab label="Active Notifications" />
          </Tabs>
        </Box>

        {activeTab === 0 && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Text
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={notificationText}
              onChange={(e) => setNotificationText(e.target.value)}
              placeholder="Enter notification text here..."
              variant="outlined"
              style={{ marginBottom: 20 }}
            />
            
            <Box display="flex" gap={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                onClick={handleCancel}
                style={{
                  minWidth: 200,
                  textTransform: "none",
                  borderColor: "#367c2b",
                  color: "#367c2b",
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleConfirm}
                disabled={!notificationText.trim() || isSubmitting}
                style={{
                  minWidth: 200,
                  textTransform: "none",
                  backgroundColor: (!notificationText.trim() || isSubmitting) ? "#9e9e9e" : "#367c2b",
                  color: "#ffffff",
                  cursor: (!notificationText.trim() || isSubmitting) ? "not-allowed" : "pointer",
                }}
              >
                {isSubmitting ? "Submitting..." : "Confirm"}
              </Button>
            </Box>
          </Box>
        )}

        {activeTab === 1 && (
          <Box>
            {notifications.length === 0 ? (
              <Typography 
                variant="body1" 
                color="textSecondary" 
                align="center"
                style={{ padding: 40 }}
              >
                No active notifications
              </Typography>
            ) : (
              <Box className={styles.tableWrapper}>
                <TableContainer component={Paper} elevation={0}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell style={{ width: '60%', minWidth: 300 }}><strong>Notification Text</strong></TableCell>
                        <TableCell align="center" style={{ width: '20%', minWidth: 120 }}><strong>Created Date</strong></TableCell>
                        <TableCell align="center" style={{ width: '20%', minWidth: 100 }}><strong>Action</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {notifications.map((notification) => (
                        <TableRow key={notification.id}>
                          <TableCell style={{ width: '60%', minWidth: 300 }}>{notification.text}</TableCell>
                          <TableCell align="center" style={{ width: '20%', minWidth: 120 }}>
                            {dayjs(notification.createdAt).format("DD-MM-YYYY")}
                          </TableCell>
                          <TableCell align="center" style={{ width: '20%', minWidth: 100 }}>
                            <IconButton
                              onClick={() => handleDelete(notification.id)}
                              color="error"
                              size="small"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default NotificationModal;
